import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import { showSuccess, showError } from "../../../toastify";

const AttendanceDialog = ({ showDialog, setShowDialog, cohortVenue, cohort }) => {
  const [attendanceDate, setAttendanceDate] = useState(new Date());
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const minDate = useMemo(() => {
    if (!cohort?.startDate) return undefined;
    const d = new Date(cohort.startDate);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [cohort?.startDate]);

  const maxDate = useMemo(() => {
    if (!cohort?.endDate) return undefined;
    const d = new Date(cohort.endDate);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [cohort?.endDate]);

  const fetchAttendance = async () => {
    if (!cohortVenue?.id) return;
    setLoading(true);
    try {
      const dateIso =
        attendanceDate instanceof Date && !Number.isNaN(attendanceDate.getTime())
          ? attendanceDate.toISOString()
          : new Date().toISOString();

      const { data: attendanceData } = await axios.get(
        `/api/cpd/training-attendance/${cohortVenue.id}?attendanceDate=${dateIso}`,
      );

      const list = Array.isArray(cohortVenue.trainees) ? cohortVenue.trainees : [];
      const updatedTrainees = list.map((trainee) => {
        const record = Array.isArray(attendanceData)
          ? attendanceData.find((a) => a.traineeId === trainee.id)
          : null;
        const mark = record?.mark || (record?.status ? "ATTENDED" : "ABSENT");
        const allowedRemarks = new Set(["With Reason", "Communicated", "Attended"]);
        const rawRemark = record?.remark;
        const remarkLabel =
          rawRemark === "Normal" ? "Attended" : rawRemark;
        const remark =
          allowedRemarks.has(remarkLabel) ? remarkLabel : remarkLabel ? "With Reason" : "Attended";
        return {
          ...trainee,
          mark: mark,
          remark: remark,
        };
      });
      setTrainees(updatedTrainees);
    } catch (error) {
      showError("Failed to fetch attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showDialog && cohortVenue) {
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog, cohortVenue?.id, attendanceDate]);

  useEffect(() => {
    if (!showDialog) setSearch("");
  }, [showDialog]);

  const handleMarkChange = (index, value) => {
    setTrainees((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], mark: value };
      return next;
    });
  };

  const handleRemarkChange = (index, value) => {
    setTrainees((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], remark: value };
      return next;
    });
  };

  const toggleAll = () => {
    setTrainees((prev) => {
      const allAttended = prev.every((t) => (t.mark || "ABSENT") === "ATTENDED");
      const nextMark = allAttended ? "ABSENT" : "ATTENDED";
      return prev.map((t) => ({ ...t, mark: nextMark }));
    });
  };

  const handleSave = async () => {
    if (!cohortVenue?.id) return;
    setSaving(true);
    try {
      const dateIso =
        attendanceDate instanceof Date && !Number.isNaN(attendanceDate.getTime())
          ? attendanceDate.toISOString()
          : new Date().toISOString();

      await axios.post("/api/cpd/training-attendance", {
        trainingCohortVenueId: cohortVenue.id,
        attendanceDate: dateIso,
        trainees: trainees.map((t) => ({
          traineeId: t.id,
          mark: t.mark === "ATTENDED" ? "ATTENDED" : "ABSENT",
          remark: t.remark || "Attended",
        })),
      });
      showSuccess("Attendance saved successfully");
      setShowDialog(false);
    } catch (error) {
      showError("Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  const attendedCount = useMemo(
    () => trainees.filter((t) => (t.mark || "ABSENT") === "ATTENDED").length,
    [trainees],
  );

  const filteredTrainees = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return trainees;

    return trainees.filter((t) => {
      const fullName = `${t?.lastName || ""} ${t?.firstName || ""}`.trim();
      const employeeId = (t?.employeeId || "").toString();
      return (
        fullName.toLowerCase().includes(q) || employeeId.toLowerCase().includes(q)
      );
    });
  }, [trainees, search]);

  const markChip = (mark) => (
    <Chip
      size="small"
      variant="outlined"
      label={mark === "ATTENDED" ? "ATTENDED" : "ABSENT"}
      color={mark === "ATTENDED" ? "success" : "default"}
      sx={{ mr: 1, fontWeight: 600, letterSpacing: 0.2 }}
    />
  );

  return (
    <Dialog
      open={!!showDialog}
      onClose={() => setShowDialog(false)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle className="text-uppercase">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>Mark Attendance</span>
          <Chip
            size="small"
            label={`${attendedCount}/${trainees.length || 0} attended`}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <span style={{ fontWeight: 600 }}>
            {attendanceDate?.toLocaleDateString?.() || ""}
          </span>
        </Box>
        <IconButton
          aria-label="close"
          onClick={() => setShowDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              label="Attendance Date"
              inputFormat="dd/MM/yyyy"
              value={attendanceDate}
              onChange={(newValue) => setAttendanceDate(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
              minDate={minDate}
              maxDate={maxDate}
            />
          </LocalizationProvider>
          <Button variant="outlined" size="small" onClick={toggleAll}>
            {attendedCount === trainees.length && trainees.length > 0
              ? "Mark all ABSENT"
              : "Mark all ATTENDED"}
          </Button>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value || "")}
            placeholder="Search by name or employee ID…"
            sx={{ minWidth: 280, flexGrow: 1, maxWidth: 520 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span className="material-icons" style={{ fontSize: 18 }}>
                    search
                  </span>
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => setSearch("")}
                    edge="end"
                  >
                    <span className="material-icons" style={{ fontSize: 18 }}>
                      close
                    </span>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {!!search && (
            <Chip
              size="small"
              variant="outlined"
              label={`${filteredTrainees.length}/${trainees.length} shown`}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              width: "100%",
              maxHeight: "52vh",
              "& .MuiTableCell-head": { fontWeight: 700, bgcolor: "grey.50" },
            }}
          >
            <Table
              stickyHeader
              size="small"
              aria-label="attendance table"
              sx={{ width: "100%", minWidth: 900 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 220 }}>Mark</TableCell>
                  <TableCell>Names</TableCell>
                  <TableCell sx={{ width: 140 }}>Employee ID</TableCell>
                  <TableCell sx={{ width: 360 }}>Remark</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTrainees.map((trainee) => {
                  const index = trainees.findIndex((t) => t.id === trainee.id);
                  const value =
                    trainee.mark === "ATTENDED" ? "ATTENDED" : "ABSENT";
                  return (
                    <TableRow
                      key={trainee.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {markChip(value)}
                          <FormControl variant="standard" sx={{ minWidth: 120 }}>
                            <Select
                              value={value}
                              onChange={(e) =>
                                handleMarkChange(index, e.target.value)
                              }
                            >
                              <MenuItem value="ATTENDED">ATTENDED</MenuItem>
                              <MenuItem value="ABSENT">ABSENT</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ fontWeight: 600 }}>
                          {trainee.lastName} {trainee.firstName}
                        </Box>
                      </TableCell>
                      <TableCell>{trainee.employeeId}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <FormControl variant="standard" sx={{ minWidth: 160 }}>
                            <Select
                              value={
                                ["With Reason", "Communicated", "Attended"].includes(
                                  trainee.remark,
                                )
                                  ? trainee.remark
                                  : "Attended"
                              }
                              onChange={(e) =>
                                handleRemarkChange(index, e.target.value)
                              }
                            >
                              <MenuItem value="Attended">Attended</MenuItem>
                              <MenuItem value="Communicated">Communicated</MenuItem>
                              <MenuItem value="With Reason">With Reason</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDialog(false)}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Attendance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceDialog;
